import {
  $applyNodeReplacement,
  TextNode,
  type EditorConfig,
  type LexicalNode,
  type NodeKey,
  type SerializedTextNode,
} from 'lexical';
import { mentionChip } from './mention.css';

export type SerializedMentionNode = SerializedTextNode;

/**
 * A committed mention (`@Label`) inside the Composer editor. A `TextNode` in
 * `token` mode so the caret treats it as one atomic unit (arrow/backspace step
 * over the whole chip), styled as a real inline pill. `getTextContent()` returns
 * its `@Label` text, so serialising the editor to a plain string round-trips the
 * mention with no special handling.
 */
export class MentionNode extends TextNode {
  static override getType(): string {
    return 'mention';
  }

  static override clone(node: MentionNode): MentionNode {
    return new MentionNode(node.__text, node.__key);
  }

  constructor(text: string, key?: NodeKey) {
    super(text, key);
  }

  override createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config);
    dom.classList.add(mentionChip);
    return dom;
  }

  static override importJSON(serialized: SerializedMentionNode): MentionNode {
    const node = $createMentionNode(serialized.text);
    node.setFormat(serialized.format);
    node.setStyle(serialized.style);
    return node;
  }

  override exportJSON(): SerializedMentionNode {
    return { ...super.exportJSON(), type: 'mention' };
  }

  /** The chip is atomic — text can't be typed into either edge of it. */
  override canInsertTextBefore(): boolean {
    return false;
  }

  override canInsertTextAfter(): boolean {
    return false;
  }
}

export function $createMentionNode(text: string): MentionNode {
  const node = new MentionNode(text);
  node.setMode('token');
  return $applyNodeReplacement(node);
}

export function $isMentionNode(node: LexicalNode | null | undefined): node is MentionNode {
  return node instanceof MentionNode;
}
